/** Keep recently finished matches prominent on Home (longer than live-sync poll buffer). */
export const HOME_RECENTLY_FINISHED_GRACE_MS = 15 * 60 * 1000;

/** @param {Record<string, unknown> | null | undefined} game */
export function isGameLive(game) {
  const status = game?.status;
  return status === 'live' || status === 'in_play' || status === 'active';
}

/**
 * @param {Record<string, unknown> | null | undefined} game
 * @param {number} [now]
 * @param {number} [graceMs]
 */
export function isRecentlyFinished(game, now = Date.now(), graceMs = HOME_RECENTLY_FINISHED_GRACE_MS) {
  if (game?.status !== 'finished') return false;
  const terminalAt = Number(game.terminalAt);
  if (!Number.isFinite(terminalAt)) return false;
  return now <= terminalAt + graceMs;
}

/**
 * Live or within the post-full-time grace window — stays near the top on Home.
 * @param {Record<string, unknown> | null | undefined} game
 * @param {number} [now]
 */
export function isProminentOnHome(game, now = Date.now()) {
  return isGameLive(game) || isRecentlyFinished(game, now);
}

function kickoffMs(game) {
  const n = Number(game?.gameDate);
  return Number.isFinite(n) ? n : Infinity;
}

function terminalMs(game) {
  const n = Number(game?.terminalAt);
  return Number.isFinite(n) ? n : null;
}

function gameIdKey(game) {
  return String(game?.gameId ?? '');
}

function homeDisplayRank(game, now) {
  if (isProminentOnHome(game, now)) return 0;
  const status = game?.status;
  if (status === 'locked') return 1;
  if (status === 'finished') return 3;
  if (status === 'postponed') return 4;
  if (status === 'canceled' || status === 'cancelled') return 4;
  return 2;
}

/**
 * @param {Record<string, unknown>} a
 * @param {Record<string, unknown>} b
 * @param {number} now
 */
export function compareGamesForHomeDisplay(a, b, now = Date.now()) {
  const rankA = homeDisplayRank(a, now);
  const rankB = homeDisplayRank(b, now);
  if (rankA !== rankB) return rankA - rankB;

  if (rankA === 0) {
    const liveA = isGameLive(a);
    const liveB = isGameLive(b);
    if (liveA !== liveB) return liveA ? -1 : 1;
    if (liveA && liveB) {
      const kickDiff = kickoffMs(a) - kickoffMs(b);
      if (kickDiff !== 0) return kickDiff;
      return gameIdKey(a).localeCompare(gameIdKey(b));
    }
    const termA = terminalMs(a) ?? 0;
    const termB = terminalMs(b) ?? 0;
    if (termA !== termB) return termB - termA;
    return gameIdKey(a).localeCompare(gameIdKey(b));
  }

  if (rankA === 3) {
    const termA = terminalMs(a);
    const termB = terminalMs(b);
    if (termA != null && termB != null && termA !== termB) return termB - termA;
    const kickDiff = kickoffMs(b) - kickoffMs(a);
    if (kickDiff !== 0) return kickDiff;
    return gameIdKey(a).localeCompare(gameIdKey(b));
  }

  const kickDiff = kickoffMs(a) - kickoffMs(b);
  if (kickDiff !== 0) return kickDiff;
  return gameIdKey(a).localeCompare(gameIdKey(b));
}

/**
 * @param {Array<Record<string, unknown>>} games
 * @param {number} [now]
 */
export function sortGamesForHomeDisplay(games, now = Date.now()) {
  if (!Array.isArray(games) || games.length === 0) return [];
  return [...games].sort((a, b) => compareGamesForHomeDisplay(a, b, now));
}

/**
 * @param {Record<string, unknown>} a
 * @param {Record<string, unknown>} b
 */
export function compareGamesForPicksDisplay(a, b) {
  const liveA = isGameLive(a);
  const liveB = isGameLive(b);
  if (liveA !== liveB) return liveA ? -1 : 1;
  const kickDiff = kickoffMs(a) - kickoffMs(b);
  if (kickDiff !== 0) return kickDiff;
  return gameIdKey(a).localeCompare(gameIdKey(b));
}

/**
 * @param {Array<Record<string, unknown>>} games
 */
export function sortGamesForPicksDisplay(games) {
  if (!Array.isArray(games) || games.length === 0) return [];
  return [...games].sort(compareGamesForPicksDisplay);
}

function isTerminalPicksGame(game) {
  const status = String(game?.status || '').toLowerCase();
  return (
    status === 'finished' ||
    status === 'postponed' ||
    status === 'canceled' ||
    status === 'cancelled'
  );
}

/**
 * Live: earliest-started live match (+ count of other live games).
 * Else: earliest non-terminal fixture by kickoff.
 * @param {Array<Record<string, unknown>>} games
 * @returns {{ match: Record<string, unknown>, extraLiveCount: number } | null}
 */
export function pickNextMatchForPicks(games) {
  if (!Array.isArray(games) || games.length === 0) return null;

  const liveGames = games
    .filter(isGameLive)
    .sort((a, b) => {
      const kickDiff = kickoffMs(a) - kickoffMs(b);
      if (kickDiff !== 0) return kickDiff;
      return gameIdKey(a).localeCompare(gameIdKey(b));
    });
  if (liveGames.length > 0) {
    return { match: liveGames[0], extraLiveCount: liveGames.length - 1 };
  }

  const next = sortGamesForPicksDisplay(games).find((game) => !isTerminalPicksGame(game));
  return next ? { match: next, extraLiveCount: 0 } : null;
}

/**
 * @param {Array<Record<string, unknown>>} games
 * @param {number} [limit]
 * @param {number} [now]
 */
export function pickFeaturedGamesForHome(games, limit = 3, now = Date.now()) {
  return sortGamesForHomeDisplay(games, now).slice(0, limit);
}

/**
 * Whether Home should tick display order (grace expiry) without refetching.
 * @param {Array<Record<string, unknown>>} games
 * @param {number} [now]
 */
export function needsHomeDisplayClock(games, now = Date.now()) {
  if (!Array.isArray(games) || games.length === 0) return false;
  return games.some(
    (game) =>
      isGameLive(game) ||
      (game?.status === 'finished' &&
        Number.isFinite(Number(game.terminalAt)) &&
        now <= Number(game.terminalAt) + HOME_RECENTLY_FINISHED_GRACE_MS)
  );
}
